import { apiClient } from '@/lib/api-client';
import type { ApiResponse, Organization, PaginatedResponse, Invite } from '@/types/api'; // ApiResponse used by getOrganizationById, createOrganization, etc.

export interface GetOrgsParams {
  search?: string;
  page?: number;
  perPage?: number;
}

export async function getOrganizations(params: GetOrgsParams = {}): Promise<PaginatedResponse<Organization>> {
  const response = await apiClient.get<PaginatedResponse<Organization>>('/organizations', { params });
  return response.data;
}

export async function getAccessibleOrganizations(params: GetOrgsParams = {}): Promise<PaginatedResponse<Organization>> {
  const response = await apiClient.get<PaginatedResponse<Organization>>('/organizations/accessible', { params });
  return response.data;
}

export async function getOrganizationById(id: string): Promise<Organization> {
  const response = await apiClient.get<ApiResponse<Organization>>(`/organizations/${id}`);
  return response.data.data!;
}

export async function createOrganization(data: { name: string; slug: string }): Promise<Organization> {
  const response = await apiClient.post<ApiResponse<Organization>>('/organizations', data);
  return response.data.data!;
}

export async function updateOrganization(id: string, data: { name?: string; slug?: string }): Promise<void> {
  await apiClient.put(`/organizations/${id}`, data);
}

export async function deleteOrganization(id: string): Promise<void> {
  await apiClient.delete(`/organizations/${id}`);
}

export async function getOrgInvites(orgId: string): Promise<Invite[]> {
  const response = await apiClient.get<ApiResponse<Invite[]>>('/invites', {
    ...(orgId ? { headers: { 'X-Active-Org-Id': orgId } } : {}),
  });
  return response.data.data ?? [];
}

export async function sendInvite(data: { email: string; role: string }): Promise<Invite> {
  const response = await apiClient.post<ApiResponse<Invite>>('/invites', data);
  return response.data.data!;
}

export async function revokeInvite(inviteId: string): Promise<void> {
  await apiClient.delete(`/invites/${inviteId}`);
}

export async function addOrgMember(
  orgId: string,
  data: { email: string; role: string },
): Promise<void> {
  await apiClient.post(`/organizations/${orgId}/members`, data, {
    headers: { 'X-Active-Org-Id': orgId },
  });
}

export async function removeOrgMember(orgId: string, userId: string): Promise<void> {
  await apiClient.delete(`/organizations/${orgId}/members/${userId}`, {
    headers: { 'X-Active-Org-Id': orgId },
  });
}
